// packages/ui/src/components/kanban/KanbanBoard.tsx
// Main Kanban board with columns grouped by card status, with drag-and-drop

import { useState, useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { KanbanColumn, type ColumnConfig } from './KanbanColumn'
import { KanbanCard } from './KanbanCard'
import type { DashboardCard } from '../../api/client'

export const COLUMNS: ColumnConfig[] = [
  { id: 'inbox', label: 'Inbox', color: 'bg-gray-400' },
  { id: 'research', label: 'Research', color: 'bg-purple-400' },
  { id: 'planned', label: 'Planned', color: 'bg-orange-400' },
  { id: 'approved', label: 'Approved', color: 'bg-green-400' },
  { id: 'executing', label: 'In Progress', color: 'bg-blue-400' },
  { id: 'verifying', label: 'Needs Review', color: 'bg-yellow-400' },
  { id: 'done', label: 'Done', color: 'bg-emerald-400' },
  { id: 'failed', label: 'Failed', color: 'bg-red-400' },
  { id: 'blocked', label: 'Blocked', color: 'bg-red-300' },
]

// Valid transitions matching backend service.ts
const VALID_TRANSITIONS: Record<string, string[]> = {
  inbox: ['research', 'blocked'],
  research: ['planned', 'blocked', 'failed'],
  planned: ['approved', 'blocked', 'failed'],
  approved: ['executing', 'blocked'],
  executing: ['verifying', 'failed', 'blocked'],
  verifying: ['done', 'failed', 'blocked'],
  done: [],
  failed: ['executing', 'inbox'],
  blocked: [],
}

export interface KanbanBoardProps {
  cards: DashboardCard[]
  onCardClick?: (card: DashboardCard) => void
  onStatusChange?: (cardId: string, newStatus: string) => void
}

export function KanbanBoard({ cards, onCardClick, onStatusChange }: KanbanBoardProps) {
  const [activeCard, setActiveCard] = useState<DashboardCard | null>(null)
  const [approvalPending, setApprovalPending] = useState<{
    cardId: string
    newStatus: string
  } | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  // Group cards by column status
  const grouped = new Map<string, DashboardCard[]>()
  for (const col of COLUMNS) {
    grouped.set(col.id, [])
  }
  for (const card of cards) {
    const bucket = grouped.get(card.column)
    if (bucket) {
      bucket.push(card)
    } else {
      grouped.get('inbox')?.push(card)
    }
  }

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const card = cards.find((c) => c.id === event.active.id)
      setActiveCard(card ?? null)
    },
    [cards],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveCard(null)
      const { active, over } = event
      if (!over) return

      const cardId = active.id as string
      const targetColumn = over.id as string

      // Find the source card
      const card = cards.find((c) => c.id === cardId)
      if (!card) return

      // Don't do anything if dropped in the same column
      // Map card.column to its display column for fallback cards
      let sourceColumn = card.column
      if (!COLUMNS.find((c) => c.id === sourceColumn)) {
        sourceColumn = 'inbox'
      }
      if (sourceColumn === targetColumn) return

      // Validate transition
      const allowed = VALID_TRANSITIONS[sourceColumn]
      if (!allowed?.includes(targetColumn)) return

      // Approval confirmation for moving to approved
      if (targetColumn === 'approved') {
        setApprovalPending({ cardId, newStatus: targetColumn })
        return
      }

      onStatusChange?.(cardId, targetColumn)
    },
    [cards, onStatusChange],
  )

  const handleApprovalConfirm = useCallback(() => {
    if (approvalPending) {
      onStatusChange?.(approvalPending.cardId, approvalPending.newStatus)
      setApprovalPending(null)
    }
  }, [approvalPending, onStatusChange])

  const handleApprovalCancel = useCallback(() => {
    setApprovalPending(null)
  }, [])

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          data-testid="kanban-board"
          className="flex gap-4 overflow-x-auto pb-4"
        >
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.id}
              config={col}
              cards={grouped.get(col.id) ?? []}
              onCardClick={onCardClick}
            />
          ))}
        </div>

        <DragOverlay>
          {activeCard ? (
            <div className="opacity-80 rotate-2">
              <KanbanCard card={activeCard} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Approval confirmation modal */}
      {approvalPending && (
        <div
          data-testid="approval-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={handleApprovalCancel}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-100 mb-2">
              Approve this task?
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Moving this card to the Approved column will mark it for execution.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="approval-cancel"
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                onClick={handleApprovalCancel}
              >
                Cancel
              </button>
              <button
                data-testid="approval-confirm"
                className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white rounded transition-colors"
                onClick={handleApprovalConfirm}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
